import type {
  AdaptivePlayerProfile,
  AdaptiveProfileRepository,
  CompletedHandRecord,
  MatchRepository,
  PlayerStatsSummary,
  PreferencesRepository,
  Ruleset,
  RulesetRepository,
  SavedMatch,
  StatsRepository,
  UserPreferences
} from './types.js';
import { derivePlayerStatsSummary } from './derive-player-stats.js';

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function createInMemoryMatchRepository(): MatchRepository {
  const store = new Map<string, SavedMatch>();

  return {
    async save(match) {
      store.set(match.id, cloneValue(match));
    },
    async get(id) {
      const match = store.get(id);
      return match ? cloneValue(match) : null;
    },
    async list() {
      return [...store.values()].map(m => cloneValue(m));
    },
    async delete(id) {
      store.delete(id);
    },
    async rename(id, name) {
      const match = store.get(id);
      if (!match) return;
      store.set(id, { ...match, name, updatedAt: new Date().toISOString() });
    }
  };
}

export function createInMemoryRulesetRepository(): RulesetRepository {
  const store = new Map<string, Ruleset>();

  return {
    async save(ruleset) {
      store.set(ruleset.id, cloneValue(ruleset));
    },
    async get(id) {
      const ruleset = store.get(id);
      return ruleset ? cloneValue(ruleset) : null;
    },
    async list() {
      return [...store.values()].map(r => cloneValue(r));
    },
    async delete(id) {
      store.delete(id);
    }
  };
}

export function createInMemoryPreferencesRepository(
  defaults: UserPreferences
): PreferencesRepository {
  let current = cloneValue(defaults);

  return {
    async load() {
      return cloneValue(current);
    },
    async save(value) {
      current = cloneValue(value);
    }
  };
}

export function createInMemoryStatsRepository(): StatsRepository {
  const hands: CompletedHandRecord[] = [];

  return {
    async appendCompletedHand(hand) {
      if (hands.some((entry) => entry.id === hand.id)) {
        return;
      }
      hands.push(cloneValue(hand));
    },
    async hasCompletedHand(id) {
      return hands.some((entry) => entry.id === id);
    },
    async getSummary(): Promise<PlayerStatsSummary> {
      return derivePlayerStatsSummary(hands);
    },
    async reset() {
      hands.length = 0;
    }
  };
}

export function createInMemoryAdaptiveProfileRepository(
  emptyProfile: AdaptivePlayerProfile
): AdaptiveProfileRepository {
  let current = cloneValue(emptyProfile);

  return {
    async load() {
      return cloneValue(current);
    },
    async save(profile) {
      current = cloneValue(profile);
    },
    async reset() {
      current = cloneValue(emptyProfile);
    }
  };
}
